import { useEffect, useMemo, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import {
  ShoppingBag,
  Star,
  Truck,
  User,
  Heart,
  ChevronRight,
  Gift,
  Calendar,
  Package,
  Copy,
  Wallet,
} from "lucide-react";
import { Link } from "react-router-dom";
import axios from "@/utils/axios";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  getUserDetails,
  checkEmailVerificationStatus,
  resendVerificationEmail,
  selectUserDetails,
  selectUserDetailsLoading,
} from "@/store/slices/authSlice";
import {
  getUserOrders,
  selectUserOrders,
  selectUserOrdersLoading,
} from "@/store/slices/orderSlice";
import {
  fetchBestSellers,
  selectBestSellers,
  selectBestSellersLoading,
} from "@/store/slices/productSlice";
import { toast } from "sonner";
import { formatCurrency } from "@/utils/format";
import { Skeleton } from "@/components/ui/skeleton";
import { useNavigate } from "react-router-dom";

export default function Overview() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const userDetails = useAppSelector(selectUserDetails);
  const userDetailsLoading = useAppSelector(selectUserDetailsLoading);
  const orders = useAppSelector(selectUserOrders);
  const ordersLoading = useAppSelector(selectUserOrdersLoading);
  const bestSellers = useAppSelector(selectBestSellers);
  const bestSellersLoading = useAppSelector(selectBestSellersLoading);

  const [isVerified, setIsVerified] = useState(true);
  const [referralData, setReferralData] = useState(null);
  const [referralLoading, setReferralLoading] = useState(false);

  const userName = userDetails
    ? `${userDetails.firstName || ""} ${userDetails.lastName || ""}`.trim() ||
      "User"
    : "User";

  // Fetch user details, orders, and best sellers on mount
  useEffect(() => {
    const fetchData = async () => {
      await Promise.all([
        dispatch(getUserDetails()),
        dispatch(getUserOrders({ page: 1, limit: 5 })), // Fetch recent 5 orders
        dispatch(fetchBestSellers(3)), // Fetch 3 best sellers for recommendations
      ]);

      // Check email verification status
      try {
        const result = await dispatch(checkEmailVerificationStatus()).unwrap();
        setIsVerified(result.isVerified ?? true);
      } catch (error) {
        console.error("Error checking verification status:", error);
        setIsVerified(true); // Default to verified on error
      }
    };

    const fetchReferralData = async () => {
      setReferralLoading(true);
      try {
        const response = await axios.get("/referral/stats");
        if (response.data.success) {
          setReferralData(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching referral stats:", error);
      } finally {
        setReferralLoading(false);
      }
    };

    fetchData();
    fetchReferralData();
  }, [dispatch]);

  // Get loyalty points from userDetails or use default
  const loyaltyPoints =
    userDetails?.loyaltyPoints ?? userDetails?.loyalty_points ?? 0;

  // Calculate next delivery date from orders or use default
  const nextDeliveryDate = useMemo(() => {
    if (orders && orders.length > 0) {
      // Find the most recent order with a delivery date
      const orderWithDelivery = orders
        .filter(
          (order) =>
            order.status !== "Cancelled" && order.status !== "Delivered",
        )
        .sort((a, b) => {
          const dateA = new Date(a.createdAt || a.created_at || 0);
          const dateB = new Date(b.createdAt || b.created_at || 0);
          return dateB - dateA;
        })[0];

      if (orderWithDelivery) {
        // Check if order has shipments with estimated delivery
        const shipment = orderWithDelivery.shipments?.[0];
        if (shipment?.estimated_delivery) {
          return new Date(shipment.estimated_delivery).toLocaleDateString(
            "en-US",
            {
              month: "long",
              day: "numeric",
              year: "numeric",
            },
          );
        }
      }
    }
    return "No upcoming deliveries";
  }, [orders]);

  // Transform orders to match the component's expected format
  const recentOrders = useMemo(() => {
    if (!orders || orders.length === 0) return [];

    return orders.slice(0, 2).map((order) => {
      const orderId = order.order_id || order.id || `ORD-${order._id}`;
      const orderDate = order.createdAt || order.created_at;
      const formattedDate = orderDate
        ? new Date(orderDate).toLocaleDateString("en-US", {
            month: "long",
            day: "numeric",
            year: "numeric",
          })
        : "N/A";

      const items = order.items || order.order_items || [];
      const total = parseFloat(order.total || 0);
      const status = order.status || "Pending";

      // Get product images and names from order items
      const products = items.map((item) => ({
        name: item.product_name || item.product?.name || item.name || "Product",
        image:
          item.product_image_url ||
          item.product?.image ||
          item.image ||
          "/placeholder.svg?height=40&width=40",
      }));

      return {
        id: orderId,
        date: formattedDate,
        total,
        status,
        items: items.length,
        products:
          products.length > 0
            ? products
            : [
                {
                  name: "Product",
                  image: "/placeholder.svg?height=40&width=40",
                },
              ],
      };
    });
  }, [orders]);

  const isLoading = userDetailsLoading || ordersLoading;

  // Transform best sellers for display
  const recommendedProducts = useMemo(() => {
    if (!bestSellers || bestSellers.length === 0) return [];

    return bestSellers.slice(0, 3).map((product) => {
      const hasDiscount = product.discount > 0;
      const finalPrice = hasDiscount
        ? product.price - (product.price * product.discount) / 100
        : product.price;

      return {
        id: product.productId || product.id,
        name: product.name,
        price: finalPrice,
        originalPrice: hasDiscount ? product.price : null,
        discount: product.discount || 0,
        image:
          product.image ||
          product.thumbnail ||
          "/placeholder.svg?height=100&width=100",
        rating: product.average_rating || product.averageRating || 0,
      };
    });
  }, [bestSellers]);

  // const isLoading = userDetailsLoading || ordersLoading; // Removed global loading check

  return (
    <div className="flex flex-col gap-6">
      {/* Welcome Section */}
      {!isVerified && (
        <div className="mb-6 bg-red-100 text-red-800 rounded-lg p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <p>
              Please verify your email address to unlock full account access.
            </p>
            <Button
              variant="outline"
              onClick={async () => {
                try {
                  const result = await dispatch(
                    resendVerificationEmail(),
                  ).unwrap();
                  toast.success(result.message || "Verification email resent.");
                } catch (error) {
                  toast.error(error || "Failed to resend email.");
                }
              }}
            >
              Resend Verification Email
            </Button>
          </div>
        </div>
      )}
      <Card className="bg-gradient-to-r from-primary/5 to-secondary/10 border-secondary/20">
        <CardHeader>
          <CardTitle className="text-3xl font-bold text-primary flex items-center gap-2">
            Welcome,{" "}
            {userDetailsLoading ? (
              <Skeleton className="h-8 w-48 inline-block" />
            ) : (
              userName
            )}
            !
          </CardTitle>
          <CardDescription className="text-gray-700">
            Here's a quick overview of your Fayoz account.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* <div className="flex items-center gap-4">
            <Gift className="w-8 h-8 text-secondary" />
            <div>
              <p className="text-sm text-gray-600">Loyalty Points</p>
              <p className="text-2xl font-bold text-primary">{loyaltyPoints}</p>
            </div>
          </div> */}
          <div className="flex items-center gap-4">
            <Calendar className="w-8 h-8 text-secondary" />
            <div>
              <p className="text-sm text-gray-600">Next Delivery</p>
              <p className="text-2xl font-bold text-primary">
                {ordersLoading ? (
                  <Skeleton className="h-7 w-32" />
                ) : (
                  nextDeliveryDate
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Package className="w-8 h-8 text-secondary" />
            <div>
              <p className="text-sm text-gray-600">Total Orders</p>
              <p className="text-2xl font-bold text-primary">
                {ordersLoading ? (
                  <Skeleton className="h-7 w-12" />
                ) : (
                  orders?.length || 0
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Wallet className="w-8 h-8 text-secondary" />
            <div>
              <p className="text-sm text-gray-600">Referral Wallet</p>
              <p className="text-2xl font-bold text-primary">
                {referralLoading ? (
                  <Skeleton className="h-7 w-24" />
                ) : (
                  formatCurrency(referralData?.balance || 0)
                )}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Refer & Earn Section */}
      <Card className="border-secondary/20 overflow-hidden">
        <div className="bg-secondary/10 px-6 py-4 border-b border-secondary/10 flex justify-between items-center">
          <div>
            <CardTitle className="text-lg font-bold text-primary flex items-center gap-2">
              <Gift className="w-5 h-5" /> Refer & Earn
            </CardTitle>
            <CardDescription>
              Invite your friends and earn commissions on their orders.
            </CardDescription>
          </div>
          <Badge
            variant="secondary"
            className="bg-secondary/20 text-secondary border-none"
          >
            {referralLoading ? (
              <Skeleton className="h-4 w-16" />
            ) : (
              `${referralData?.referral_count || 0} Referrals`
            )}
          </Badge>
        </div>
        <CardContent className="pt-6">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-xl border border-dashed border-gray-300 relative group">
                <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold mb-1">
                  Your Referral Code
                </p>
                <div className="flex justify-between items-center">
                  <p className="text-2xl font-mono font-bold text-primary tracking-widest">
                    {referralLoading ? (
                      <Skeleton className="h-8 w-32" />
                    ) : (
                      referralData?.referral_code || "-------"
                    )}
                  </p>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="hover:bg-secondary/20 text-secondary"
                    onClick={() => {
                      if (referralData?.referral_code) {
                        navigator.clipboard.writeText(
                          referralData.referral_code,
                        );
                        toast.success("Referral code copied!");
                      }
                    }}
                  >
                    <Copy className="w-4 h-4 mr-2" /> Copy
                  </Button>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <div className="bg-primary/5 px-3 py-1 rounded-full text-xs font-medium text-primary">
                  Earn{" "}
                  {referralLoading
                    ? "..."
                    : referralData?.referral_percentage || 0}
                  % commission from every order
                </div>
                <div className="bg-primary/5 px-3 py-1 rounded-full text-xs font-medium text-primary">
                  {referralLoading ? (
                    <Skeleton className="h-4 w-28" />
                  ) : (
                    "Instant Wallet Payouts"
                  )}
                </div>
              </div>
            </div>
            <div className="bg-primary/5 p-6 rounded-2xl flex flex-col items-center text-center gap-4">
              <div className="space-y-1">
                <p className="text-sm text-gray-600">Total Reward Earned</p>
                <p className="text-3xl font-black text-primary">
                  {referralLoading ? (
                    <Skeleton className="h-9 w-32 mx-auto" />
                  ) : (
                    formatCurrency(referralData?.lifetime_earned || 0)
                  )}
                </p>
              </div>
              <Button
                className="w-full bg-secondary hover:bg-secondary/90 text-white shadow-lg shadow-secondary/20"
                onClick={() => navigate("/dashboard/referrals")}
              >
                Withdraw Funds & View History
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-primary">Quick Actions</CardTitle>
          <CardDescription>
            Jump to important sections of your dashboard.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          <Link to="/shop">
            <Button
              variant="outline"
              className="w-full h-24 flex flex-col items-center justify-center gap-2 border-secondary text-secondary hover:bg-secondary hover:text-white bg-transparent"
            >
              <ShoppingBag className="w-6 h-6" />
              <span className="text-sm font-medium">Shop Now</span>
            </Button>
          </Link>
          <Link to="/dashboard/orders">
            <Button
              variant="outline"
              className="w-full h-24 flex flex-col items-center justify-center gap-2 border-secondary text-secondary hover:bg-secondary hover:text-white bg-transparent"
            >
              <Truck className="w-6 h-6" />
              <span className="text-sm font-medium">Track Order</span>
            </Button>
          </Link>
          <Link to="/dashboard/settings">
            <Button
              variant="outline"
              className="w-full h-24 flex flex-col items-center justify-center gap-2 border-secondary text-secondary hover:bg-secondary hover:text-white bg-transparent"
            >
              <User className="w-6 h-6" />
              <span className="text-sm font-medium">Edit Profile</span>
            </Button>
          </Link>
        </CardContent>
      </Card>

      {/* Recent Orders */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-primary">Recent Orders</CardTitle>
            <CardDescription>Your latest purchases from Fayoz.</CardDescription>
          </div>
          <Link to="/dashboard/orders">
            <Button
              variant="ghost"
              className="text-secondary hover:text-secondary/80"
            >
              View All <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </CardHeader>
        <CardContent className="space-y-4">
          {ordersLoading ? (
            [1, 2].map((i) => (
              <div key={i}>
                <div className="flex items-center justify-between py-3">
                  <div className="flex items-center gap-4">
                    <Skeleton className="h-10 w-20 rounded-full" />
                    <div>
                      <Skeleton className="h-5 w-32 mb-1" />
                      <Skeleton className="h-4 w-48" />
                    </div>
                  </div>
                  <div className="text-right">
                    <Skeleton className="h-5 w-20 mb-1 ml-auto" />
                    <Skeleton className="h-6 w-16 ml-auto" />
                  </div>
                </div>
                {i < 2 && <Separator />}
              </div>
            ))
          ) : recentOrders.length > 0 ? (
            recentOrders.map((order, index) => (
              <div key={order.id}>
                <div className="flex items-center justify-between py-3">
                  <div className="flex items-center gap-4">
                    <div className="flex -space-x-2 overflow-hidden">
                      {order.products.slice(0, 3).map((product, pIndex) => (
                        <img
                          key={pIndex}
                          src={product.image || "/placeholder.svg"}
                          alt={product.name}
                          width={40}
                          height={40}
                          className="inline-block h-10 w-10 rounded-full ring-2 ring-white object-cover"
                        />
                      ))}
                      {order.products.length > 3 && (
                        <div className="inline-flex items-center justify-center h-10 w-10 rounded-full ring-2 ring-white bg-gray-200 text-xs font-medium text-gray-600">
                          +{order.products.length - 3}
                        </div>
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-primary">
                        Order {order.id}
                      </p>
                      <p className="text-sm text-gray-600">
                        {order.date} • {order.items} items
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-primary">
                      {formatCurrency(order.total)}
                    </p>
                    <Badge
                      className={`mt-1 ${
                        order.status === "Delivered"
                          ? "bg-green-100 text-green-700"
                          : "bg-secondary/10 text-secondary"
                      }`}
                    >
                      {order.status}
                    </Badge>
                  </div>
                </div>
                {index < recentOrders.length - 1 && <Separator />}
              </div>
            ))
          ) : (
            <p className="text-gray-500 text-center py-4">
              No recent orders found. Start shopping now!
            </p>
          )}
        </CardContent>
      </Card>

      {/* Personalized Recommendations */}
      <Card>
        <CardHeader>
          <CardTitle className="text-primary">
            Personalized Recommendations
          </CardTitle>
          <CardDescription>
            Our best-selling products, trusted by thousands of customers.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {bestSellersLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="flex flex-col items-center text-center p-4 rounded-lg bg-gray-50"
                >
                  <Skeleton className="w-24 h-24 rounded-lg mb-3" />
                  <Skeleton className="h-4 w-32 mb-2" />
                  <Skeleton className="h-4 w-16 mb-3" />
                  <Skeleton className="h-8 w-24" />
                </div>
              ))}
            </div>
          ) : recommendedProducts.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
              {recommendedProducts.map((product) => (
                <div
                  key={product.id}
                  className="flex flex-col items-center text-center p-4 rounded-lg bg-gray-50 hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => navigate(`/products/${product.id}`)}
                >
                  <img
                    src={product.image || "/placeholder.svg"}
                    alt={product.name}
                    width={100}
                    height={100}
                    className="rounded-lg mb-3 object-cover"
                  />
                  <h3 className="font-semibold text-primary mb-1 line-clamp-2">
                    {product.name}
                  </h3>
                  <div className="flex items-center gap-1 text-sm text-gray-600 mb-2">
                    <Star className="w-4 h-4 fill-secondary text-secondary" />
                    <span>{product.rating.toFixed(1)}</span>
                  </div>
                  <div className="mb-3">
                    {product.discount > 0 ? (
                      <>
                        <p className="text-sm text-gray-500 line-through">
                          {formatCurrency(product.originalPrice)}
                        </p>
                        <p className="font-bold text-primary">
                          {formatCurrency(product.price)}
                        </p>
                      </>
                    ) : (
                      <p className="font-bold text-primary">
                        {formatCurrency(product.price)}
                      </p>
                    )}
                  </div>
                  <Button
                    className="bg-secondary hover:bg-secondary/90 text-primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/products/${product.id}`);
                    }}
                  >
                    View Product
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">
              No recommended products available at the moment.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
